import { useEffect, useState } from "react";
import { MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import api from "@/api";
import Seo, { breadcrumbSchema, localBusinessSchema, webPageSchema } from "@/components/Seo";

const CITY_IMG = "/outlet-interior.webp";

const Locations = () => {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="locations-page">
      <Seo
        title="Chai Times Locations | Kulhad Chai Cafés Across Bengaluru"
        description="Find Chai Times outlets in Kammanahalli, HRBR Layout, Hegdenagar, Bhartiya City and Kasturi Nagar. Open daily from 8 AM to midnight."
        path="/locations"
        image="/outlet-interior.webp"
        schema={[
          webPageSchema({
            type: "CollectionPage",
            name: "Chai Times Locations in Bengaluru",
            description: "Five Chai Times kulhad chai cafés across Bengaluru, open daily from 8 AM to midnight.",
            path: "/locations",
          }),
          {
            "@type": "ItemList",
            itemListElement: locations.map((location, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: localBusinessSchema(location),
            })),
          },
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }]),
        ]}
      />
      <section className="relative pt-36 md:pt-44 pb-24 bg-forest text-cream overflow-hidden grain">
        <img src={CITY_IMG} alt="Chai Times outlet interior" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <SectionTag dark>Outlets</SectionTag>
            <h1 className="font-serif font-black text-5xl md:text-7xl tracking-tight mt-5 leading-[0.98]">
              Five addas.<br /><span className="italic text-gold">One Bengaluru.</span>
            </h1>
            <p className="mt-6 text-cream/70 max-w-lg leading-relaxed">
              Every outlet brews the same slow-simmered recipe. Find the one closest to your corner of the city.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hours-note">
              <span className="rounded-full border border-gold/40 bg-gold/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-gold">
                Outlets: 8 AM – 12 AM, all days
              </span>
              <span className="rounded-full border border-cream/20 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-cream/60">
                Head Office: 10 AM – 6 PM, Mon–Fri
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-6">
          {locations.map((l, i) => (
            <Reveal key={l.id} delay={i * 0.07}>
              <div className="group border border-clayline rounded-2xl bg-cream p-8 hover:shadow-xl transition-all duration-300 h-full" data-testid={`location-card-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-terracotta">{String(i + 1).padStart(2, "0")} — {l.area}</span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.name + " " + l.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`location-map-${i}`}
                    className="w-10 h-10 shrink-0 rounded-full border border-clayline flex items-center justify-center group-hover:bg-terracotta group-hover:text-cream group-hover:border-terracotta transition-colors"
                    aria-label="Open in maps"
                  >
                    <ArrowUpRight size={16} />
                  </a>
                </div>
                <h2 className="font-serif font-bold text-2xl md:text-3xl mt-4 mb-4">{l.name}</h2>
                <p className="text-charcoal/65 flex gap-2.5 text-sm leading-relaxed"><MapPin size={16} className="shrink-0 mt-0.5 text-terracotta" />{l.address}</p>
                <p className="text-charcoal/65 flex gap-2.5 items-center mt-3 text-sm"><Clock size={16} className="text-terracotta" />{l.hours}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Locations;
